import { useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Trash2 } from "lucide-react";
import { useDeletePayment } from "@/lib/commercialQueries";
import { SimpleDeleteConfirm } from "../shared/SimpleDeleteConfirm";

const STATUS_STYLES = {
  paid: "bg-emerald-100 text-emerald-700",
  pending: "bg-mustard/20 text-ink",
  failed: "bg-red-100 text-red-700",
  refunded: "bg-ink/10 text-ink-soft",
};

function StatusBadge({ status }) {
  return (
    <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 capitalize ${STATUS_STYLES[status] || "bg-ink/10 text-ink-soft"}`}>
      {status}
    </span>
  );
}

/**
 * Master v5 Prompt 14 — PaymentRecord history/status + next scheduled
 * payment. Read-only display; no "pay" action exists in this Prompt's
 * scope (manual coach entry only) — this satisfies "View as Client
 * cannot pay" by construction, for every actor, not just View as Client.
 * Prompt 30D — a coach (never a client) can permanently delete a
 * payment record.
 */
export function PaymentsCard({ workspaceId, payments, nextPayment, isClientActor }) {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const deletePayment = useDeletePayment(workspaceId);

  async function handleConfirmDelete() {
    try {
      await deletePayment.mutateAsync(deleteTarget.pmt_id);
      toast.success("Payment record deleted");
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't delete that payment");
    }
  }

  return (
    <div className="border border-ink/10 rounded-lg p-4 bg-paper" data-testid="commercial-payments-card">
      <h2 className="font-serif text-lg text-ink mb-2">Payments</h2>
      {nextPayment && (
        <div className="flex items-center gap-2 text-sm bg-terracotta/10 rounded px-3 py-2 mb-3" data-testid="commercial-next-payment">
          <CalendarClock size={14} className="text-terracotta shrink-0" />
          <span className="text-ink">
            Next payment: <span className="font-medium">{nextPayment.currency} {nextPayment.amount.toLocaleString()}</span> due {new Date(nextPayment.due_at).toLocaleDateString()}
          </span>
        </div>
      )}
      {payments.length === 0 ? (
        <p className="text-sm text-ink-soft italic" data-testid="commercial-payments-empty">No payment history yet.</p>
      ) : (
        <div className="space-y-1.5">
          {payments.map((p) => (
            <div key={p.pmt_id} className="flex items-center justify-between text-sm" data-testid={`commercial-payment-row-${p.pmt_id}`}>
              <span className="text-ink-soft truncate">{p.description || "Payment"}</span>
              <span className="flex items-center gap-2 shrink-0">
                <span className="text-ink font-medium">{p.currency} {p.amount.toLocaleString()}</span>
                <StatusBadge status={p.status} />
                {!isClientActor && (
                  <button onClick={() => setDeleteTarget(p)} title="Delete permanently" data-testid={`commercial-payment-delete-${p.pmt_id}`}>
                    <Trash2 size={13} className="text-ink-soft hover:text-red-600" />
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      <SimpleDeleteConfirm
        open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete this payment record permanently?"
        description="This can't be undone."
        onConfirm={handleConfirmDelete} isPending={deletePayment.isPending}
        testId="commercial-payment-delete-confirm"
      />
    </div>
  );
}
